import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import CTABand from "@/components/sections/CTABand";

function renderMarkdown(md) {
  // Minimal markdown rendering: headings, bold, lists
  const lines = md.split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-heading font-bold text-2xl text-[#0B1F3A] mt-8 mb-3">{line.replace("## ", "")}</h2>;
    if (line.startsWith("# ")) return <h1 key={i} className="font-heading font-black text-3xl text-[#0B1F3A] mt-8 mb-3">{line.replace("# ", "")}</h1>;
    if (line.match(/^\d+\.\s/)) return <li key={i} className="ml-6 list-decimal text-[#4B5563]" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s/, "").replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#0B1F3A]'>$1</strong>") }} />;
    if (line.startsWith("- ")) return <li key={i} className="ml-6 list-disc text-[#4B5563]" dangerouslySetInnerHTML={{ __html: line.replace("- ", "").replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#0B1F3A]'>$1</strong>") }} />;
    if (line.trim() === "") return <br key={i} />;
    return <p key={i} className="text-[#4B5563] leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#0B1F3A]'>$1</strong>") }} />;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-32 text-center"><p>Article non trouvé. <Link to="/blog" className="text-[#00B8D9] underline">Retour au blog</Link></p></div>;
  if (!post) return <div className="py-32 text-center text-[#4B5563]">Chargement...</div>;

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.cover_image || undefined,
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "articleSection": post.category,
          "author": {"@type": "Organization", "name": "Passerelle Numérique Inc."},
          "publisher": {"@type": "Organization", "name": "Passerelle Numérique Inc.", "logo": {"@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_b3aef13b-f596-4d0d-a8af-4fa0fa7ae7f3/artifacts/o5rk853q_Logo%20Passerelle%20Num%20Inc.png"}},
          "mainEntityOfPage": `https://passerellenumerique.ca/blog/${post.slug}`,
        }}
      />
      <article className="bg-white pt-12 pb-16">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <Link to="/blog" className="text-sm text-[#00B8D9] font-semibold hover:underline flex items-center gap-1" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4" /> Retour au blog
          </Link>

          <div className="mt-6 flex items-center gap-3 text-xs">
            <span className="px-3 py-1.5 rounded-full bg-[#00B8D9]/10 text-[#00B8D9] font-bold tracking-wider uppercase">{post.category}</span>
            <span className="text-[#4B5563] flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.created_at).toLocaleDateString("fr-CA")}</span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F3A] tracking-tighter mt-4 text-balance">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-[#4B5563]">{post.excerpt}</p>

          {post.cover_image && (
            <div className="mt-8 rounded-2xl overflow-hidden border border-gray-200">
              <img src={post.cover_image} alt={post.title} className="w-full aspect-video object-cover" />
            </div>
          )}

          <div className="mt-10 prose-pn">
            {renderMarkdown(post.content)}
          </div>
        </div>
      </article>
      <CTABand />
    </div>
  );
}
